<template>
  <div class="tw-min-h-screen" style="background-color: #eaeed3;">
    <AppBar />

    <main>
    <!-- Header -->
    <div class="tw-text-center tw-px-6 tw-pt-12 tw-pb-6">
      <h1
        class="tw-font-semibold tw-uppercase tw-tracking-widest"
        style="color: #346a7e; font-family: 'Roboto Condensed', sans-serif; font-size: clamp(1.2rem, 2.2vw, 2rem); line-height: 1.3;"
      >
        Science-Based Encyclopedia of Nutrition and Natural Medicine
        Improving Health Through Knowledge
      </h1>
    </div>

    <!-- Hero: text left, image right -->
    <div class="tw-flex tw-flex-col lg:tw-flex-row tw-gap-8 tw-px-8 lg:tw-px-16 tw-pb-8">
      <div
        class="tw-flex-1 tw-text-justify tw-leading-snug"
        style="font-family: 'Roboto Condensed', sans-serif; font-size: 1rem; font-weight: 400;"
      >
        <p class="tw-mb-3">
          Green Medicine Encyclopedia is a comprehensive resource on human health, covering a wide range of topics related to various body ailments.
        </p>
        <p class="tw-mb-3">
          Our goal at Green Medicine Encyclopedia is to provide trustworthy, evidence-based information on natural health, helping people learn about the therapeutic benefits of vitamins, minerals, herbs, and foods, and how they support maintaining or achieving good health. We believe that knowledge is essential for empowering individuals to take control of their health and well-being. Individuals learn about the therapeutic benefits of vitamins, minerals, herbs, and foods, and how they contribute to maintaining or achieving good health. We believe knowledge is essential in empowering individuals to take control of their health and well-being.
        </p>
        <p>
          It includes suggested foods, herbs, and nutritional supplements that can help treat, prevent, or alleviate these conditions. It also offers information on improving the functioning of specific organs through diet and nutrition. Moreover, it outlines the key nutrients in different foods and herbs and their therapeutic uses. Additionally, readers can learn about essential nutrients, such as amino acids, bioflavonoids, carbohydrates, enzymes, fatty acids, vitamins, and minerals, and how they benefit the human body. Lastly, the encyclopedia discusses the functions of hormones, neurotransmitters, enzymes, cells, and proteins within the body. Green Medicine Encyclopedia's content includes hyperlinks to relevant related topics with evidence-based references.
        </p>
      </div>
      <div class="tw-w-full lg:tw-w-[46%] tw-flex-shrink-0 tw-mt-6 lg:tw-mt-0">
        <HeroCarousel
          height="440"
          class="tw-w-full tw-rounded-[30px]"
          style="box-shadow: 0 4px 24px rgba(0,0,0,0.13);"
        />
      </div>
    </div>

    <!-- Extended text -->
    <div
      class="tw-px-8 lg:tw-px-16 tw-pb-8 tw-text-justify tw-leading-snug"
      style="font-family: 'Roboto Condensed', sans-serif; font-size: 1rem; font-weight: 400;"
    >
      <p class="tw-mb-2">The human body has the power to heal itself, and reverse chronic illness.</p>
      <p class="tw-mb-2">The body doesn't come with a manual—learning is the anti-aging intervention.</p>
      <p class="tw-mb-2">Find what really works to prevent disease and rebuild your health naturally.</p>
      <p class="tw-mb-2">
        Researching your own health is crucial because conventional medicine is designed mainly for acute emergencies and broad population guidelines, not for personalized, preventive, and longevity-focused care. It tends to respond to symptoms after a disease has appeared, often with drugs that control rather than address the underlying causes. Self-guided learning allows you to intervene early—before disease develops—using precision nutrition, lifestyle changes, and low-risk botanicals that mainstream systems often overlook.
      </p>
      <p class="tw-mb-2">
        Bottom line: Mainstream medicine keeps you alive; self-research keeps you vital. The system is a safety net, not a blueprint. Your body is the ultimate clinical trial—run it like a scientist.
      </p>
      <p class="tw-mb-2">
        Today, the public recognizes that traditional medicines do not provide all the answers to their health questions. The desire for more knowledge has led to the rapid growth of the alternative healthcare field. People are very aware that the fuels they put in their bodies have a powerful effect on their mood, metabolism, and even their mental health.
      </p>
      <p>
        As we age, our bodies go through natural physiological changes—such as decreased metabolic efficiency, hormonal shifts, increased oxidative stress, and declining organ function—that make us more susceptible to chronic diseases, frailty, and a shorter lifespan. Learning about nutrients, vitamins, minerals, and herbs helps us fight these changes with evidence-based dietary and supplement strategies.
      </p>
    </div>

    <!-- The Values Grid Section -->
    <div class="tw-relative tw-max-w-5xl tw-mx-auto tw-my-16 tw-px-4 lg:tw-px-8" style="font-family: 'Roboto Condensed', sans-serif;">
      
      <!-- The Grid -->
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 md:tw-gap-6">
        <!-- Top Left -->
        <ValuesGridCard 
          theme="light" 
          roundedCorner="top-left"
          iconUrl="/landing_page/psychiatry.svg"
          title="We Bring Clarity To The Complexity Of Nature"
        />

        <!-- Top Right -->
        <ValuesGridCard 
          theme="dark" 
          roundedCorner="top-right"
          iconUrl="/landing_page/experiment.svg"
          title="We Honor Tradition With Scientific Validation"
          description="While we respect centuries of traditional use, we believe true reliability comes from scientific validation. We investigate the clinical evidence behind each natural remedy to provide a complete, 360-degree view you can trust."
        />

        <!-- Bottom Left -->
        <ValuesGridCard 
          theme="dark" 
          roundedCorner="bottom-left"
          iconUrl="/landing_page/encrypted.svg"
          title="Empower You To Make Confident Safe Decision"
        />

        <!-- Bottom Right -->
        <ValuesGridCard 
          theme="light" 
          roundedCorner="bottom-right"
          iconUrl="/landing_page/approval_delegation.svg"
          title="We Are Your Single Source Of Truth"
        />
      </div>

      <!-- Center Circle -->
      <div class="tw-hidden md:tw-flex tw-absolute tw-top-1/2 tw-left-1/2 tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2 tw-w-64 tw-h-64 tw-rounded-full tw-flex-col tw-shadow-2xl tw-overflow-hidden " style="border: 1px solid rgba(45, 106, 79, 0.15);">
        <!-- Faded Background -->
        <div class="tw-absolute tw-inset-0 tw-z-0" style="background-color: rgba(245, 248, 234, 0.95); backdrop-filter: blur(8px); -webkit-mask-image: linear-gradient(to right, black 55%, transparent 100%); mask-image: linear-gradient(to right, black 55%, transparent 100%);"></div>
        
        <!-- Text content -->
        <div class="tw-relative tw-z-10 tw-w-full tw-h-full tw-flex tw-flex-col tw-justify-center tw-items-start tw-pl-8 lg:tw-pl-10 tw-text-left">
          <p class="tw-text-[#346a7e] tw-text-xl lg:tw-text-2xl tw-font-normal" style="line-height: 1.2;">Trusted Knowledge,</p>
          <p class="tw-text-[#346a7e] tw-text-xl lg:tw-text-2xl tw-font-normal" style="line-height: 1.2;">Natural Solutions.</p>
          <p class="tw-text-[#2d6a4f] tw-text-2xl lg:tw-text-3xl tw-font-bold tw-mt-2">Better Health</p>
        </div>
      </div>

      
    </div>

    <footer class="tw-px-8 tw-pb-8 tw-pt-4" style="color: rgba(0,0,0,0.75); font-family: 'Roboto', sans-serif; font-size: 1.1rem;">
      {{ new Date().getFullYear() }} &copy; Green Medicine Works
    </footer>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useSeo } from "@/composables/useSeo";

useSeo({
  title: "About",
  description: "Science-Based Encyclopedia of Nutrition and Natural Medicine — Improving Health Through Knowledge",
});
</script>

<!--
  Roboto Condensed and Inter are loaded asynchronously from index.html
  to avoid render-blocking. Do not re-import them here.
-->

<route lang="yaml">
meta:
  layout: non-auth.vue
</route>
